import React, { useState, useContext } from 'react';
import _ from 'lodash';
import { styled } from '@this/constants/themes';
import { observer } from 'mobx-react';
import { Link, Redirect } from 'react-router-dom';
import axios from 'axios';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Divider from '@material-ui/core/Divider';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import MuiLink from '@material-ui/core/Link';
import CancelIcon from '@material-ui/icons/Cancel';
import SettingsEthernetIcon from '@material-ui/icons/SettingsEthernet';
import PeopleAltIcon from '@material-ui/icons/PeopleAlt';
import PaymentIcon from '@material-ui/icons/Payment';
import HelpIcon from '@material-ui/icons/Help';
import LaunchIcon from '@material-ui/icons/Launch';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import KeyboardArrowRightIcon from '@material-ui/icons/KeyboardArrowRight';
import FormatListBulletedIcon from '@material-ui/icons/FormatListBulleted';
import PublicIcon from '@material-ui/icons/Public';
import Button from '@material-ui/core/Button';
import Tooltip from '@material-ui/core/Tooltip';
import Drawer from '@material-ui/core/Drawer';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import muiTheme from '@this/components/appoint/mui_theme';
import type Calendar from '@this/domain/appoint/calendar';
import AppointContext from '@this/components/appoint/appoint_context';
import DrawerServiceMenu from '@this/components/appoint/drawer_service_menu';

interface Props {
  onNavigate: () => void;
  setOpen: (value: boolean) => void;
  onAdjustmentlistOpen?: (e: React.MouseEvent) => void;
  onOpenMeetingDrawerOpen?: () => void;
  selectedCalendars?: Calendar[];
  handleSelectedCalendarsChange?: (calendar: Calendar, checked: boolean) => void;
}

const DrawerMenu = observer(
  ({
    onNavigate,
    setOpen,
    onAdjustmentlistOpen,
    onOpenMeetingDrawerOpen,
    selectedCalendars,
    handleSelectedCalendarsChange
  }: Props) => {
    const contextValue = useContext(AppointContext);
    const user = contextValue.user;
    const [redirectPath, setRedirectPath] = useState<string | null>(null);
    const [openDrawer, setOpenDrawer] = useState<boolean>(false);

    const handleSignOut = async () => {
      await axios.get('/aicalendar/users/sign_out.json');
      contextValue.onUserSignedOut();
      setRedirectPath('/users/sign_in'); // Router範囲内なのでhistory.createRefは使わない
    };

    const handleAdjustmentlistOpen = (e: React.MouseEvent) => {
      if (!onAdjustmentlistOpen) {
        setRedirectPath('/#drawerOpened');
        return;
      }

      setOpen(false);
      onAdjustmentlistOpen(e);
    };

    const handleOpenMeetingDrawerOpen = () => {
      if (!onOpenMeetingDrawerOpen) {
        setRedirectPath('/#openMeetingDrawer');
        return;
      }

      setOpen(false);
      onOpenMeetingDrawerOpen();
    };

    return (
      <>
        {redirectPath && <Redirect to={redirectPath} />}
        <DrawerBox>
          <Box padding="20px">
            <CloseButton onClick={() => setOpen(false)} />
            {user ? (
              <>
                {contextValue.isMobile ? (
                  <div style={{ marginTop: '-12px' }}>
                    <DashboardButtonWrap>
                      <DashboardButton
                        variant="contained"
                        color="primary"
                        href="/aicalendar/adjustments/new"
                        startIcon={<AddCircleOutlineIcon />}
                        endIcon={<KeyboardArrowRightIcon />}
                      >
                        <span style={{ margin: '0 auto' }}>新規作成</span>
                      </DashboardButton>
                    </DashboardButtonWrap>
                    <DashboardButtonWrap>
                      <DashboardButton
                        variant="text"
                        onClick={e => handleAdjustmentlistOpen(e)}
                        startIcon={<FormatListBulletedIcon />}
                        endIcon={
                          onAdjustmentlistOpen ? (
                            <span style={{ width: 20, height: 20 }} />
                          ) : (
                            <KeyboardArrowRightIcon />
                          )
                        }
                      >
                        <span style={{ margin: '0 auto' }}>調整一覧</span>
                      </DashboardButton>
                    </DashboardButtonWrap>
                    <DashboardButtonWrap>
                      <DashboardButton
                        variant="text"
                        onClick={() => handleOpenMeetingDrawerOpen()}
                        startIcon={<PublicIcon />}
                        endIcon={
                          onAdjustmentlistOpen ? (
                            <span style={{ width: 20, height: 20 }} />
                          ) : (
                            <KeyboardArrowRightIcon />
                          )
                        }
                      >
                        <Box marginTop="-1px">公開URL一覧</Box>
                      </DashboardButton>
                    </DashboardButtonWrap>
                    {onAdjustmentlistOpen && handleSelectedCalendarsChange && (
                      <Box position="relative">
                        {Object.values(_.groupBy(contextValue.calendars, c => c.editable)).map((group, i) => (
                          <div key={i} style={{ marginBottom: '2px' }}>
                            <CalendarsTitle>{i === 0 ? 'マイカレンダー' : '他のカレンダー'}</CalendarsTitle>
                            {group.map((c, j) => (
                              <Box
                                key={`${i}_${j}_${c.id}`}
                                style={{
                                  marginLeft: '-5px',
                                  marginTop: '0px',
                                  overflow: 'hidden',
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={
                                        _.filter(selectedCalendars, sc => sc.id.indexOf(c.id) > -1).length > 0
                                      }
                                      onChange={e => handleSelectedCalendarsChange(c, e.target.checked)}
                                      size="small"
                                      style={{
                                        padding: '0px 3px 0px 2px',
                                        color:
                                          _.filter(selectedCalendars, sc => sc.id.indexOf(c.id) > -1).length > 0
                                            ? c.backgroundColor || muiTheme.palette.secondary.main
                                            : '#dddddd'
                                      }}
                                    />
                                  }
                                  label={
                                    <span style={{ fontSize: '12px', letterSpacing: '0.05rem', color: '#606060' }}>
                                      {c.name + (c.provider === 'outlook' && c.email ? ` (${c.email})` : '')}
                                    </span>
                                  }
                                  style={{ margin: '0' }}
                                />
                              </Box>
                            ))}
                          </div>
                        ))}
                      </Box>
                    )}
                  </div>
                ) : (
                  <>
                    {user.organization && (
                      <Typography style={{ marginBottom: '12px', fontSize: '11px' }}>
                        {user.organization.name}
                      </Typography>
                    )}
                    <Typography style={{ fontSize: '16px', fontWeight: 'bold' }}>{user.name}</Typography>
                    <Typography color="textSecondary" style={{ fontSize: '12px' }}>
                      {user.email}
                    </Typography>
                    <LogoutButton size="small" variant="text" onClick={() => handleSignOut()}>
                      ログアウト
                    </LogoutButton>
                  </>
                )}
              </>
            ) : (
              <>
                <Typography style={{ marginBottom: '12px', fontSize: '12px' }}>
                  AIカレンダーに登録またはログイン
                </Typography>
                <Button
                  style={{ display: 'block', margin: '5px 0 0 -10px' }}
                  onClick={() => {
                    location.href = contextValue.googleOauthPath;
                  }}
                >
                  <img src="/images/sign_in_with_google.png" width="191" />
                </Button>
                <Button
                  style={{ display: 'block', margin: '-5px 0 0 -7px' }}
                  onClick={() => {
                    location.href = contextValue.outlookOauthPath;
                  }}
                >
                  <img src="/images/sign_in_with_microsoft.png" width="215" />
                </Button>
              </>
            )}
          </Box>
          <Divider />
          <Box style={{ marginBottom: 'auto', padding: '12px 4px' }}>
            {user && (
              <>
                {contextValue.isMobile && onAdjustmentlistOpen && (
                  <Typography style={{ padding: '8px 16px', fontSize: '16px', fontWeight: 'bold' }}>
                    {user.name}
                  </Typography>
                )}
                <ListItem button onClick={() => setOpenDrawer(true)}>
                  <TheListItemIcon>
                    <SettingsEthernetIcon color="primary" />
                  </TheListItemIcon>
                  <TheListItemText primary="サービス連携" color="primary" />
                </ListItem>
                <Tooltip
                  title={
                    user.aicalendarAdmin ? '' : 'このページにアクセスするにはこのチームの管理者権限が必要です'
                  }
                >
                  <div>
                    <ListItem
                      button
                      component={Link}
                      to="/organization/members"
                      onClick={() => onNavigate()}
                      disabled={!user.aicalendarAdmin}
                    >
                      <TheListItemIcon>
                        <PeopleAltIcon color="primary" />
                      </TheListItemIcon>
                      <TheListItemText primary="ユーザー管理" color="primary" />
                    </ListItem>
                  </div>
                </Tooltip>
              </>
            )}
            <ListItem button onClick={() => window.open('https://help.aicalendar.cloud/', '_blank')}>
              <TheListItemIcon>
                <HelpIcon color="primary" />
              </TheListItemIcon>
              <TheListItemText primary="ヘルプ" color="primary" style={{ width: '50px' }} />
              <TheListItemIcon style={{ width: '120px' }}>
                <LaunchIcon color="primary" fontSize="small" />
              </TheListItemIcon>
            </ListItem>
            {user && (
              <Tooltip
                title={user.aicalendarAdmin ? '' : 'このページにアクセスするにはこのチームの管理者権限が必要です'}
              >
                <div style={{ display: 'none' }}>
                  <ListItem
                    button
                    component={Link}
                    to="/organization/payments"
                    onClick={() => onNavigate()}
                    disabled={!user.aicalendarAdmin}
                  >
                    <TheListItemIcon>
                      <PaymentIcon />
                    </TheListItemIcon>
                    <TheListItemText primary="お支払い" color="primary" />
                  </ListItem>
                </div>
              </Tooltip>
            )}
            {user && contextValue.isMobile && onAdjustmentlistOpen && (
              <div style={{ padding: '8px 16px' }}>
                <LogoutButton size="small" variant="text" onClick={() => handleSignOut()}>
                  ログアウト
                </LogoutButton>
              </div>
            )}
          </Box>
          <Divider />
          <CompanyBox>
            <CompanyLink href="https://transferdata.co.jp/about">運営会社</CompanyLink>
            <CompanyLink href="https://aitravel.cloud/aicalendar/tos/">利用規約</CompanyLink>
            <CompanyLink href="https://aitravel.cloud/privacy-policy">個人情報保護方針</CompanyLink>
            <CompanyLink href="https://aitravel.cloud/sct">特定商取引に関する法律に基づく表記</CompanyLink>
          </CompanyBox>
        </DrawerBox>
        <Drawer
          open={openDrawer}
          anchor={contextValue.isMobile ? 'left' : 'right'}
          onClose={() => setOpenDrawer(false)}
        >
          <DrawerServiceMenu onClose={() => setOpenDrawer(false)} />
        </Drawer>
      </>
    );
  }
);

const DrawerBox = styled(Box)`
  display: flex;
  flex-direction: column;
  min-width: 240px;
  height: 100vh;
`;

const CloseButton = styled(CancelIcon)`
  display: block;
  margin: 0 0 24px auto;
  color: #ddd;
  cursor: pointer;
`;

const DashboardButtonWrap = styled(Box)`
  margin-bottom: 10px;
`;

const DashboardButton = styled(Button)`
  width: 170px;
  padding: 10px 14px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: bold;
  line-height: 20px;
`;

const CalendarsTitle = styled(Typography)`
  padding-top: 20px;
  padding-bottom: 5px;
  color: #606060;
  font-weight: bold;
  font-size: 14px;
  letter-spacing: 0.05rem;
  line-height: 20px;
`;

const LogoutButton = styled(Button)`
  margin-top: 10px;
  padding: 0;
  color: ${props => props.theme.grayTextColor};
`;

const TheListItemIcon = styled(ListItemIcon)`
  min-width: 30px;
`;

const TheListItemText = styled(ListItemText)`
  margin: 6px 0 2px;
  font-size: 14px;

  .MuiTypography-displayBlock {
    font-weight: bold;
  }
`;

const CompanyBox = styled(Box)`
  padding: 14px 12px;
  background-color: ${props => props.theme.grayBgColor};
`;

const CompanyLink = styled(Button).attrs({ component: MuiLink, target: '_blank' })`
  display: block;
  color: ${props => props.theme.grayTextColor};
  font-size: 11px;
`;

export default DrawerMenu;
